/**
 * De onde vem o dado das Promoções, e como dizer isso na tela.
 *
 * Mesmo princípio do `pages/reclamacoes/fonte.ts`, que já está em produção:
 *
 *   "O que não pode acontecer é a analista olhar para um dado de setembro achando que é de hoje."
 *
 * Hoje é **Nível 1** (JSON estático, ver GUIA_artifact_para_tela_fiesta-bi.md §4): o arquivo é
 * gerado pelos scripts em `scripts/promocoes_ml/` e copiado à mão para `public/data/`.
 * A tela **precisa** mostrar `gerado_em` — a coleta completa leva ~1h50 e não é diária.
 *
 * ┌─ POR QUE O ARQUIVO É .json.gz ────────────────────────────────────────────────────────┐
 * │ O JSON das duas contas cru tem 16,5 MB (1.553 anúncios × ~7 promoções, cada uma com    │
 * │ comissão e frete reais). Gzipado dá 0,80 MB — 20× menor, na mesma ordem de grandeza do │
 * │ ml-score-anuncios.json (1,4 MB) que já está no repo.                                   │
 * │                                                                                        │
 * │ A descompressão é no navegador, com DecompressionStream (Chrome/Edge 80+, Firefox 113+,│
 * │ Safari 16.4+). Foi assim que o painel HTML saiu de 17,5 MB para 1,2 MB e parou de      │
 * │ congelar o Chrome — medido em 17/09/2026.                                              │
 * │                                                                                        │
 * │ ⚠️ NÃO troque por um .json cru de 16 MB contando com o gzip do servidor: o arquivo      │
 * │ ainda entraria no git com 16 MB, e o parse de 16 MB numa SPA trava a aba.              │
 * └────────────────────────────────────────────────────────────────────────────────────────┘
 */
import type { BasePromocoes } from './logic'

const ARQUIVO = '/data/promocoes-ml.json.gz'
/** Mais que isto desde a coleta completa = "COLETA ATRASADA" no subtítulo (07/10/2026; antes eram 2 dias). */
export const COLETA_ATRASADA_HORAS = 26

export interface FonteInfo {
  /** 'arquivo' hoje. Quando existir endpoint, 'api' — e a tela tem que dizer qual foi. */
  origem: 'arquivo' | 'api'
  /** ISO da coleta. É o que a tela mostra como "dados de ...". */
  geradoEm: string
}

export interface CargaPromocoes {
  base: BasePromocoes
  fonte: FonteInfo
}

/** Descomprime .gz no navegador. Sem isso, o parse de 16 MB trava a aba. */
async function lerGz(url: string): Promise<unknown> {
  const r = await fetch(url, { cache: 'no-store' })
  if (!r.ok) throw new Error(`HTTP ${r.status} ao buscar ${url}`)
  if (!r.body) throw new Error('resposta sem corpo')
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('Este navegador não tem DecompressionStream. Use Chrome/Edge 80+, Firefox 113+ ou Safari 16.4+.')
  }
  // ⚠️ O Vite (dev) manda o .gz com `Content-Encoding: gzip` e o navegador JÁ descomprime; o
  // nginx (produção) entrega os bytes crus. Descomprimir duas vezes dá "Failed to fetch". Então
  // olha os dois primeiros bytes: 1f 8b = ainda é gzip → descomprime; senão já é JSON.
  const bytes = new Uint8Array(await r.arrayBuffer())
  const aindaGzip = bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b
  if (!aindaGzip) return JSON.parse(new TextDecoder().decode(bytes))
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))
  return JSON.parse(await new Response(stream).text())
}

export async function carregaPromocoes(): Promise<CargaPromocoes> {
  const base = (await lerGz(ARQUIVO)) as BasePromocoes
  if (!base?.contas || !Object.keys(base.contas).length) {
    throw new Error('promocoes-ml.json.gz veio sem contas')
  }
  return { base, fonte: { origem: 'arquivo', geradoEm: base.gerado_em } }
}

/**
 * Subtítulo do DomainPageShell. Regra da casa: a tela sempre diz de quando é o dado e de onde
 * veio. Não remova a data para "ficar mais limpo".
 */
export function subtituloFonte(f: FonteInfo, nomeConta: string, relidoEm?: string | null): string {
  const d = new Date(f.geradoEm)
  const quando = Number.isNaN(d.getTime())
    ? f.geradoEm
    : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
  const dias = Number.isNaN(d.getTime()) ? null : Math.floor((Date.now() - d.getTime()) / 86400000)
  const idade = dias == null ? '' : dias === 0 ? ' (hoje)' : dias === 1 ? ' (ontem)' : ` (há ${dias} dias)`
  // Dois relógios (02/10/2026): a M.C. (comissão, frete, imposto, CMV) é da coleta completa das 02:30;
  // o status/preço/estoque de cada promoção é relido da API 3× ao dia e a cada rodada do robô ou
  // clique em Atualizar. A tela diz os dois — antes dizia "arquivo estático, atualização manual",
  // o que já não era verdade e escondia a releitura.
  const r = relidoEm ? new Date(relidoEm) : null
  const relido = r && !Number.isNaN(r.getTime())
    ? `status relido da API às ${r.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`
    : 'status só da coleta (sem releitura)'
  // 07/10/2026 (ela: "desenhe a correção definitiva"): o aviso só vinha com 2 dias; a coleta é diária (02:30, 2ª chance
  // às 05:00), então mais de 26 h desde a coleta já é atraso.
  const horas = Number.isNaN(d.getTime()) ? null : (Date.now() - d.getTime()) / 3600000
  const atraso = horas != null && horas > COLETA_ATRASADA_HORAS ? ' ⚠ COLETA ATRASADA' : ''
  return `${nomeConta} · M.C. da coleta completa de ${quando}${idade}${atraso} · ${relido}${
    f.origem === 'arquivo' ? ' · republicado 3× ao dia e a cada rodada' : ' · API'
  }`
}
